import { firestore } from 'firebase';
import DatabaseAdapter from 'src/firebase/db/DatabaseAdapter';
import sort from 'fast-sort';
import { formatDateToDate, copyTimeOnlyHHmmss } from 'src/helpers/UtilityHelper';
import { ReferenceError, InternalError } from 'src/firebase/db/ApplicationError';

// サブコレクション
export const notificationTemplateBakedBreadInitialEntity = {
  selected: false,
  ref: {} as firestore.DocumentReference,
  maximum_order: 0,
  stock: 0,
};
export type NotificationTemplateBakedBreadEntity = typeof notificationTemplateBakedBreadInitialEntity;
export const notificationTemplateBakedBreadEntityKeys = (Object.keys(
  notificationTemplateBakedBreadInitialEntity,
) as unknown) as (keyof NotificationTemplateBakedBreadEntity)[];

export const notificationTemplateInitialEntity = {
  id: '',
  shop_id: '',
  name: '',
  pickup_supplement: '',
  scheduled_time: undefined as Date | undefined,
  can_order_time: undefined as Date | undefined,
  last_pickup_time: undefined as Date | undefined,
  baked_breads: [] as NotificationTemplateBakedBreadEntity[],
  created_at: {} as firestore.Timestamp,
  updated_at: {} as firestore.Timestamp,
};

export type NotificationTemplateEntity = typeof notificationTemplateInitialEntity;
export const notificationTemplateEntityKeys = (Object.keys(
  notificationTemplateInitialEntity,
) as unknown) as (keyof NotificationTemplateEntity)[];

class NotificationTemplatesDB extends DatabaseAdapter {
  constructor(props: any) {
    super(props);
    this.setSchemaKeys(notificationTemplateEntityKeys);
  }

  async getNotificationTemplate(notificationTemplateId: string) {
    const notificationTemplateDocRef = await this.getDocumentRef(notificationTemplateId).get();
    const notificationTemplate = (await notificationTemplateDocRef.data()) as NotificationTemplateEntity;
    return this.formatSuccess<NotificationTemplateEntity>(notificationTemplate);
  }

  async getNotificationTemplates(payload: { shopId: string; templateIds?: string[] | undefined }) {
    const { shopId, templateIds } = payload;
    return this.getConnection()
      .where('shop_id', '==', shopId)
      .orderBy('scheduled_time', 'asc')
      .get()
      .then(async (snapshot) => {
        if (snapshot.empty) {
          console.log('No matching documents.');
          throw new ReferenceError('テンプレートが存在しません');
        }

        const rows = await Promise.all(
          snapshot.docs.map(async (doc) => (await doc.data()) as NotificationTemplateEntity),
        );

        // ソート
        let notificationTemplates = sort(rows).by([
          {
            desc: (v) => copyTimeOnlyHHmmss(formatDateToDate(v.scheduled_time)).getTime(),
          },
        ]);

        // ID指定でのフィルタリング
        if (templateIds && templateIds.length) {
          notificationTemplates = notificationTemplates.filter((v) => templateIds.includes(v.id));
        }

        return this.formatSuccess<NotificationTemplateEntity[]>(notificationTemplates);
      })
      .catch((err) => {
        console.error(err);
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  async create(query: Partial<NotificationTemplateEntity>) {
    const filteredQuery = this.filterQuery(query);
    const bakedBreads = filteredQuery.baked_breads || [];
    const requestQuery = {
      name: filteredQuery.name,
      shop_id: filteredQuery.shop_id,
      pickup_supplement: filteredQuery.pickup_supplement,
      scheduled_time: filteredQuery.scheduled_time,
      can_order_time: filteredQuery.can_order_time,
      last_pickup_time: filteredQuery.last_pickup_time,
      baked_breads: bakedBreads.filter((v) => v.selected && v.stock > 0),
      created_at: this.getServerTimeStamp(),
      updated_at: this.getServerTimeStamp(),
    };

    const ref = await this.getConnection().add(requestQuery);
    return await ref
      .update({ id: ref.id })
      .then(async () => {
        return this.formatSuccess({ ref });
      })
      .catch((err) => {
        throw new InternalError(err.message);
      });
  }

  async delete(id: string) {
    const docRef = this.getDocumentRef(id);
    const notificationTemplate = (await (await docRef.get()).data()) as NotificationTemplateEntity;
    return docRef
      .delete()
      .then(async () => {
        return this.formatSuccess(notificationTemplate);
      })
      .catch((err) => {
        console.error(err);
        throw new InternalError(err.message);
      });
  }

  async update(query: Partial<NotificationTemplateEntity>) {
    const filteredQuery = this.filterQuery(query);
    const bakedBreads = filteredQuery.baked_breads || [];
    const requestQuery = {
      name: filteredQuery.name,
      shop_id: filteredQuery.shop_id,
      pickup_supplement: filteredQuery.pickup_supplement,
      scheduled_time: filteredQuery.scheduled_time,
      can_order_time: filteredQuery.can_order_time,
      last_pickup_time: filteredQuery.last_pickup_time,
      baked_breads: bakedBreads.filter((v) => v.selected && v.stock > 0),
      updated_at: {} as firestore.Timestamp,
    };

    console.log('postQuery:notification_templates update', requestQuery);

    const ref = this.getDocumentRef(filteredQuery.id!);
    return ref
      .update(requestQuery)
      .then(async () => {
        const d = await this.getDocument(filteredQuery.id!);
        return this.formatSuccess(d);
      })
      .catch((err) => {
        console.log(err);
        throw new InternalError(err.message);
      });
  }
}

export default new NotificationTemplatesDB({
  schema: 'notification_templates',
});
